import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DigitalTwinTableSearch from "../../../../Components/DigitalTwinTableSearch";
import SOHDetailsChart from "./SOHDetailsChart";
import SOHDetailsTable from "./SOHDetailsTable";
import SOHDetailsSettingsSidebar from "./SOHDetailsSettingsSidebar";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { getSOHScatterData } from "../../../../Api/BatteryHealthApi";
import { useBaseEntityInfo, usePagesContent } from "../../../../Hooks/usePagesContent";
import CODE from "../../../../Static/Constants/StatusCodes";
import { useSOHBuckets, resolveSOHCategory } from "../utils/sohBuckets";
import { buildOdometerRangeFilter } from "../utils/tableUtils";

// ─── Component ────────────────────────────────────────────────────────────────
const SOHDetailsView = ({
  globalFilters = {},
  onBack,
  allOnboardingLocationsHierarchy = [],
  allFleets = [],
}) => {
  const { baseEntityType } = useBaseEntityInfo();
  const isVehicle = baseEntityType === "vehicle";
  const pagesContent = usePagesContent();
  const sohBuckets = useSOHBuckets();

  const sohDetailsConfig = pagesContent?.metaInfo?.BatteryIntelligence?.SOHDetails || {};
  const filtersConfig    = sohDetailsConfig.globalFilters || {};
  const chartConfig      = sohDetailsConfig.charts?.sohScatterChart || {};
  const tableConfig      = sohDetailsConfig.table || {};
  const searchableFields = sohDetailsConfig.searchableFields || [];

  const AXIS_X_TO_SELECT = { addedOn: "calendarAgeDaysEst", cycleCount: "eqCycleEst", odometer: "odometer", latestSOHPredictionOdometer: "latestSOHPredictionOdometer" };
  // Tooltip fields that map to a backend select metric (soh -> sohEst and
  // odometer are already covered by the base select / isVehicle handling).
  const TOOLTIP_KEY_TO_SELECT = { sohConfidence: "sohConfidence", latestSOHPredictionOdometer: "latestSOHPredictionOdometer", sohComputedDate: "sohComputedDate" };
  const scatterExtraSelect = useMemo(() => {
    const fromAxes     = (chartConfig.axisCombinations || []).map((c) => AXIS_X_TO_SELECT[c.x]);
    const fromTooltips = (chartConfig.tooltipFields || []).map((f) => TOOLTIP_KEY_TO_SELECT[f?.key]);
    return [...new Set([...fromAxes, ...fromTooltips].filter(Boolean))];
  }, [chartConfig.axisCombinations, chartConfig.tooltipFields]);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  // ─── Sidebar filter state ───────────────────────────────────────────────────
  const [sidebarFilters, setSidebarFilters] = useState({
    sohCategories:       [],
    stringSohCategories: [],
    stringSohConfidence: [],
    sohConfidence:       [],
    configuration:       globalFilters.configuration        || [],
    manufacturingYear:   [],
    location:            globalFilters.hierarchicalLocation || [],
    operationalStatus:   globalFilters.operationalStatus    || [],
    cellChemistry:       [],
    protocol:            [],
    fleets:              [],
    odometer:            { start: "", end: "" },
  });

  // ─── Search state ───────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });
  const searchDebounceRef = useRef(null);

  // ─── Effective global filters — merge sidebar location/configuration ────────
  const effectiveGlobalFilters = useMemo(() => {
    const merged = { ...globalFilters };
    if (sidebarFilters.configuration?.length > 0) {
      merged.configuration = sidebarFilters.configuration;
    }
    if (sidebarFilters.location?.length > 0) {
      merged.hierarchicalLocation = sidebarFilters.location;
    }
    // Business segment and customer account both arrive as fleet ids, which is what
    // becomes the fleetIDs request header.
    if (sidebarFilters.fleets?.length > 0) {
      merged.fleets = sidebarFilters.fleets;
    }
    if (sidebarFilters.operationalStatus?.length > 0) {
      merged.operationalStatus = sidebarFilters.operationalStatus;
    }
    return merged;
  }, [globalFilters, sidebarFilters.configuration, sidebarFilters.location, sidebarFilters.fleets, sidebarFilters.operationalStatus]);

  // ─── Scatter chart additional filters (manufacturing year + sohCategories + search) ─────────
  const scatterAdditionalFilters = useMemo(() => {
    const filters = [];
    if (sidebarFilters.manufacturingYear?.length > 0) {
      filters.push({ name: "mfgYear", value: sidebarFilters.manufacturingYear });
    }
    if ((sidebarFilters.sohCategories ?? []).length > 0) {
      filters.push({ name: "StateOfHealthBucket", value: sidebarFilters.sohCategories });
    }
    if (sidebarFilters.cellChemistry?.length > 0) {
      filters.push({ name: "cellChemistry", value: sidebarFilters.cellChemistry });
    }
    if (sidebarFilters.protocol?.length > 0) {
      filters.push({ name: "protocolName", value: sidebarFilters.protocol });
    }
    // Battery-level SOH confidence — values sent as stored ("High" | "Medium" | "Low")
    if (sidebarFilters.sohConfidence?.length > 0) {
      filters.push({ name: "sohConfidence", value: sidebarFilters.sohConfidence });
    }

    // String-level filters — backend matches a battery when ANY of its strings qualifies
    if (sidebarFilters.stringSohCategories?.length > 0) {
      filters.push({ name: "StringStateOfHealthBucket", value: sidebarFilters.stringSohCategories });
    }
    if (sidebarFilters.stringSohConfidence?.length > 0) {
      filters.push({ name: "stringSohConfidence", value: sidebarFilters.stringSohConfidence });
    }
    const odometerFilter = buildOdometerRangeFilter(sidebarFilters.odometer);
    if (odometerFilter) {
      filters.push(odometerFilter);
    }
    const { selectedDevices, selectedSearchColumn } = debouncedSearchTerm;
    if (selectedDevices?.length > 0) {
      filters.push({ name: selectedSearchColumn || "batteryID", value: selectedDevices });
    }
    return filters;
  }, [sidebarFilters.manufacturingYear, sidebarFilters.sohCategories, sidebarFilters.cellChemistry, sidebarFilters.protocol, sidebarFilters.stringSohCategories, sidebarFilters.stringSohConfidence, sidebarFilters.sohConfidence, sidebarFilters.odometer, debouncedSearchTerm]);

  // ─── API fetch (scatter chart data) ────────────────────────────────────────
  const [rawBatteries, setRawBatteries] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setRawBatteries([]);
      setLoading(true);
      try {
        const res = await getSOHScatterData(effectiveGlobalFilters, isVehicle, scatterAdditionalFilters, scatterExtraSelect);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items = res?.response?.data ?? [];
          setRawBatteries(items.map(({ xLabel, metricsArray, metaInfo }) => {
            const getVal = (type) => metricsArray?.find((m) => m.type === type)?.value ?? null;
            const soh = getVal("sohEst");
            return {
              batteryID:      xLabel,
              deviceID:       metaInfo?.deviceID,
              licensePlateNo: metaInfo?.licensePlateNo,
              soh,
              age:       getVal("calendarAgeDaysEst"),
              ageDays:   getVal("calendarAgeDaysEst"), // tooltip "Age" renderer reads ageDays (in days)
              bmsCycles: getVal("eqCycleEst"),
              odometer:  getVal("odometer"),
              latestSOHPredictionOdometer: getVal("latestSOHPredictionOdometer"),
              sohConfidence: getVal("sohConfidence"),
              sohComputedDate: getVal("sohComputedDate"),
              // Raw metaInfo: the category is resolved in the memo below so org meta
              // arriving later does not re-run this request.
              sohMetaInfo: metaInfo ?? null,
            };
          }));
        }
      } catch (_) {
        // batteries already cleared above; loading will be set false in finally
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [effectiveGlobalFilters, isVehicle, scatterAdditionalFilters, scatterExtraSelect]);

  // Backend label wins ("SOH Unavailable" for null / negative SOH); see resolveSOHCategory.
  const batteries = useMemo(
    () => rawBatteries.map((b) => ({
      ...b,
      category: resolveSOHCategory(b.sohMetaInfo, b.soh, sohBuckets),
    })),
    [rawBatteries, sohBuckets]
  );

  // ─── Chart legend + x-axis state ───────────────────────────────────────────
  const [selectedCategories,  setSelectedCategories]  = useState(new Set());
  const [activeXFilter,       setActiveXFilter]       = useState(null);
  const [chartTransitioning,  setChartTransitioning]  = useState(false);
  const prevXFilterRef = useRef(null);

  const handleCategorySelect = useCallback((name) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const handleResetCategories = useCallback(() => {
    setSelectedCategories(new Set());
  }, []);

  // ─── X-axis filter change — show brief loader on user-initiated switch ──────
  const handleXFilterChange = useCallback((key) => {
    if (prevXFilterRef.current !== null && prevXFilterRef.current !== key) {
      setChartTransitioning(true);
    }
    prevXFilterRef.current = key;
    setActiveXFilter(key);
  }, []);

  useEffect(() => {
    if (!chartTransitioning) return;
    const timer = setTimeout(() => setChartTransitioning(false), 350);
    return () => clearTimeout(timer);
  }, [chartTransitioning]);

  const handleSearch = useCallback((selectedDevices, selectedSearchColumn) => {
    const next = { selectedDevices, selectedSearchColumn };
    setSearchTerm(next);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearchTerm(next), 400);
  }, []);

  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  // ─── Derived: filtered batteries for the scatter chart ────────────────────
  // SOH category (sidebar) and search are server-side (scatterAdditionalFilters).
  // Only the legend selectedCategories filter is applied in-memory.
  const filteredBatteries = useMemo(() => {
    if (selectedCategories.size === 0) return batteries;
    return batteries.filter((b) => selectedCategories.has(b.category));
  }, [batteries, selectedCategories]);

  // ─── Additional API filters for the battery list table ─────────────────────
  const tableAdditionalFilters = useMemo(() => {
    const filters = [];

    // Combined SOH category filter (legend + sidebar)
    const mergedCategories = [
      ...selectedCategories,
      ...(sidebarFilters.sohCategories || []),
    ];
    const uniqueCategories = [...new Set(mergedCategories)];
    if (uniqueCategories.length > 0) {
      filters.push({ name: "StateOfHealthBucket", value: uniqueCategories });
    }

    // Manufacturing year filter — backend expects filter name "mfgYear"
    if (sidebarFilters.manufacturingYear?.length > 0) {
      filters.push({ name: "mfgYear", value: sidebarFilters.manufacturingYear });
    }

    if (sidebarFilters.cellChemistry?.length > 0) {
      filters.push({ name: "cellChemistry", value: sidebarFilters.cellChemistry });
    }

    // Protocol: the UI key is "protocol" (matching the label) but the wire name is
    // "protocolName", the column getConfigBaseQuery's protocolMeta join exposes.
    if (sidebarFilters.protocol?.length > 0) {
      filters.push({ name: "protocolName", value: sidebarFilters.protocol });
    }

    // Battery-level SOH confidence — values sent as stored ("High" | "Medium" | "Low")
    if (sidebarFilters.sohConfidence?.length > 0) {
      filters.push({ name: "sohConfidence", value: sidebarFilters.sohConfidence });
    }

    // String-level filters — backend matches a battery when ANY of its strings qualifies
    if (sidebarFilters.stringSohCategories?.length > 0) {
      filters.push({ name: "StringStateOfHealthBucket", value: sidebarFilters.stringSohCategories });
    }
    if (sidebarFilters.stringSohConfidence?.length > 0) {
      filters.push({ name: "stringSohConfidence", value: sidebarFilters.stringSohConfidence });
    }

    // Odometer range — filters the SOH-anchored odometer that the column shows, so
    // batteries whose SOH pipeline has not populated it yet drop out when a bound is set.
    const odometerFilter = buildOdometerRangeFilter(sidebarFilters.odometer);
    if (odometerFilter) {
      filters.push(odometerFilter);
    }

    // Search filter
    const { selectedDevices, selectedSearchColumn } = searchTerm;
    if (selectedDevices?.length > 0) {
      filters.push({ name: selectedSearchColumn || "batteryID", value: selectedDevices });
    }

    return filters;
  }, [selectedCategories, sidebarFilters.sohCategories, sidebarFilters.manufacturingYear, sidebarFilters.cellChemistry, sidebarFilters.protocol, sidebarFilters.stringSohCategories, sidebarFilters.stringSohConfidence, sidebarFilters.sohConfidence, sidebarFilters.odometer, searchTerm]);

  return (
    <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative", width: "100%" }}>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: isSidebarExpanded ? "calc(100% - 320px)" : "100%",
          minWidth: 0,
          transition: "width 0.3s ease-in-out",
        }}
      >
        {/* ── Back button ─────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            onClick={onBack}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              "&:hover": {
                "& .MuiTypography-root": { color: "#6D72F6 !important" },
                "& .MuiSvgIcon-root": { color: "#6D72F6" },
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
            <Typography variant="fw500s14lh24c2E2E2E">Back</Typography>
          </Box>
        </Box>

        {searchableFields.length > 0 && (
          <DigitalTwinTableSearch
            onSearch={handleSearch}
            containerWidth="80%"
            searchableFields={searchableFields}
          />
        )}

        {/* ── Chart card ──────────────────────────────────────────────────────── */}
        {chartConfig.enabled !== false && (
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              padding: "24px 22px",
              border: "1px solid #E6E6E6",
            }}
          >
            {/* Chart header: title + Reset button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#2E2E2E",
                  lineHeight: "24px",
                }}
              >
                Digital Twin based SOH
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetCategories}
                disabled={selectedCategories.size === 0}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  minWidth: "auto",
                  px: 1.5,
                  py: "3px",
                  borderRadius: "6px",
                  color: selectedCategories.size > 0 ? "#6D72F6" : "#9E9E9E",
                  borderColor: selectedCategories.size > 0 ? "#6D72F6" : "#D1D5DB",
                  "&:hover": { borderColor: "#6D72F6", bgcolor: "#F5F5FF" },
                  "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#9E9E9E" },
                }}
              >
                Reset
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "360px" }}>
                <CartLoader />
              </Box>
            ) : (
              <Box sx={{ position: "relative" }}>
                {chartTransitioning && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: "rgba(255,255,255,0.75)",
                      zIndex: 1,
                      borderRadius: "4px",
                    }}
                  >
                    <CartLoader />
                  </Box>
                )}
                <SOHDetailsChart
                  batteries={filteredBatteries}
                  selectedCategories={selectedCategories}
                  onCategorySelect={handleCategorySelect}
                  activeXFilter={activeXFilter}
                  onXFilterChange={handleXFilterChange}
                  chartConfig={chartConfig}
                />
              </Box>
            )}
          </Box>
        )}

        {/* ── Battery list table ───────────────────────────────────────────────── */}
        {tableConfig.enabled !== false && (
          <SOHDetailsTable
            globalFilters={effectiveGlobalFilters}
            additionalFilters={tableAdditionalFilters}
          />
        )}
      </Box>

      {/* ── Settings sidebar ──────────────────────────────────────────────────── */}
      <SOHDetailsSettingsSidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded((v) => !v)}
        filters={sidebarFilters}
        onFiltersChange={(patch) => setSidebarFilters((prev) => ({ ...prev, ...patch }))}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        allFleets={allFleets}
        selectedFleetIds={effectiveGlobalFilters.fleets || []}
        filtersConfig={filtersConfig}
      />
    </Box>
  );
};

export default SOHDetailsView;
